@let active = presetsStore.activePreset();

<button
  #presetTrigger
  type="button"
  class="duration-fast hover:bg-interactive mr-1.5 inline-flex h-[26px] cursor-pointer items-center gap-1.5 rounded-xs border px-2 font-sans text-xs font-semibold whitespace-nowrap transition-colors ease-out"
  [class]="
    isChipHighlighted()
      ? 'border-accent-border bg-accent-interactive text-accent-main'
      : 'border-default bg-elevated text-text-primary'
  "
  aria-haspopup="true"
  [attr.aria-expanded]="isMenuOpen()"
  (click)="toggleMenu()">
  <i
    [class]="ICONS.stack"
    [class.text-accent-main]="isChipHighlighted()"
    [class.text-text-secondary]="!isChipHighlighted()"></i>
  <span class="max-w-[160px] truncate">
    {{
      active && !presetsStore.activeTemplate()
        ? active.name
        : ('features.data.filter_presets.title' | translate)
    }}
  </span>
  <i [class]="ICONS.caretDown" class="text-[9px]"></i>
</button>

@if (presetsStore.activeTemplate()) {
  <button
    type="button"
    class="gx-token mr-1.5 cursor-pointer pr-2"
    [title]="'features.data.filter_presets.template.edit_values' | translate"
    (click)="presetsStore.reopenActivation()">
    <i [class]="ICONS.bracketsCurly" aria-hidden="true"></i>
    <span class="max-w-[240px] truncate">{{ templatePillLabel() }}</span>
  </button>
}

<app-dropdown
  [target]="presetTrigger"
  [template]="presetsMenu"
  [isOpen]="isMenuOpen()"
  [onClose]="closeMenu"
  [position]="'bottom-start'"
  [minWidth]="300"
  [useDefaultStyles]="false"
  [closeOnClickOutside]="true"
  [closeOnEscape]="true" />

<!-- After the menu's dropdown: both open on the chip, and the overlay hands a second show on an
     open target that dropdown's handle, which the menu's close would then hide. -->
<app-dropdown
  [target]="presetTrigger"
  [template]="activationPopover"
  [isOpen]="presetsStore.pendingTemplate() !== null"
  [onClose]="closeActivation"
  [position]="'bottom-start'"
  [minWidth]="340"
  [useDefaultStyles]="false"
  [closeOnClickOutside]="true"
  [closeOnEscape]="true" />

<ng-template #activationPopover>
  @if (presetsStore.pendingTemplate(); as pending) {
    <app-filter-template-activation
      [preset]="pending"
      [vocabulary]="vocabulary()"
      [initialValues]="activationValues()"
      (confirmed)="presetsStore.activateTemplate(pending, $event)"
      (cancelled)="presetsStore.cancelActivation()" />
  }
</ng-template>

<span class="bg-default mx-0.5 h-[18px] w-px"></span>

<ng-template #presetsMenu>
  <div class="g-menu">
    <div class="g-menu-search">
      <i [class]="ICONS.magnifyingGlass"></i>
      <input
        type="text"
        [value]="searchTerm()"
        [placeholder]="'features.data.filter_presets.search_placeholder' | translate"
        (input)="searchTerm.set($any($event.target).value)" />
    </div>

    <div class="g-menu-scroll">
      @for (group of groups(); track group.labelKey) {
        <p class="g-menu-label">{{ group.labelKey | translate: group.labelParams }}</p>
        @for (preset of group.presets; track preset.id) {
          <ng-container
            [ngTemplateOutlet]="presetRow"
            [ngTemplateOutletContext]="{ $implicit: preset }" />
        }
      }

      @if (!hasResults()) {
        <p class="g-menu-empty">
          {{ 'features.data.filter_presets.empty' | translate }}
        </p>
      }
    </div>

    <div class="g-menu-divider"></div>

    <div
      class="g-menu-item"
      [class.is-disabled]="!presetsStore.hasFiltersToSave()"
      role="button"
      tabindex="0"
      (click)="openCreateDialog()"
      (keydown.enter)="openCreateDialog()">
      <i [class]="ICONS.plus"></i>
      {{ 'features.data.filter_presets.save_current' | translate }}
      <span class="shortcut">
        {{
          presetsStore.hasFiltersToSave()
            ? ('features.data.filter_presets.criteria_count'
              | translate: { count: currentCriteriaCount() })
            : '—'
        }}
      </span>
    </div>

    @if (active) {
      <div
        class="g-menu-item"
        role="button"
        tabindex="0"
        (click)="detachPreset()"
        (keydown.enter)="detachPreset()">
        <i [class]="ICONS.x"></i>
        {{ 'features.data.filter_presets.detach' | translate }}
      </div>
    }
  </div>
</ng-template>

<ng-template #presetRow let-preset>
  <div
    class="g-menu-item"
    [class.is-active]="preset.id === presetsStore.activePresetId()"
    role="button"
    tabindex="0"
    (click)="applyPreset(preset)"
    (keydown.enter)="applyPreset(preset)">
    <i
      [class]="
        preset.id === presetsStore.activePresetId()
          ? ICONS.check
          : preset.template
            ? ICONS.bracketsCurly
            : preset.scope === FilterPresetScope.DATASPACE
              ? ICONS.users
              : ICONS.bookmarkSimple
      "></i>
    <span class="ellipsis">{{ preset.name }}</span>
    @if (preset.isDefault) {
      <span class="g-menu-tag">{{
        'features.data.filter_presets.default_tag' | translate
      }}</span>
    }
    @if (hasSort(preset)) {
      <i
        [class]="ICONS.sortAscending"
        class="text-text-tertiary shrink-0 text-sm"
        [attr.aria-label]="'features.data.filter_presets.has_sort' | translate"
        [title]="'features.data.filter_presets.has_sort' | translate"></i>
    }
    <span class="shortcut">
      @if (preset.template) {
        {{
          'features.data.filter_presets.template.parameter_count'
            | translate: { count: preset.parameters?.length ?? 0 }
        }}
      } @else if (criteriaCounts().get(preset.id) !== null) {
        {{
          'features.data.filter_presets.criteria_count'
            | translate: { count: criteriaCounts().get(preset.id) ?? 0 }
        }}
      }
    </span>

    @if (canWrite(preset)) {
      <span
        #rowMenuTrigger
        class="row-more"
        role="button"
        tabindex="0"
        [attr.aria-label]="'features.data.filter_presets.row_actions' | translate"
        [attr.aria-expanded]="openRowMenuId() === preset.id"
        (click)="toggleRowMenu(preset, $event)"
        (keydown.enter)="toggleRowMenu(preset, $event)">
        <i [class]="ICONS.dotsThreeVertical"></i>
      </span>
      <app-dropdown
        [target]="rowMenuTrigger"
        [template]="rowMenu"
        [context]="{ preset: preset }"
        [isOpen]="openRowMenuId() === preset.id"
        [onClose]="closeRowMenu"
        [position]="'bottom-end'"
        [minWidth]="220"
        [useDefaultStyles]="false"
        [closeOnClickOutside]="true"
        [closeOnEscape]="true" />
    }
  </div>
</ng-template>

<ng-template #rowMenu let-preset="preset">
  <div class="g-menu">
    <div class="g-menu-header">
      <span class="label">
        {{ 'features.data.filter_presets.row_menu_header' | translate }}
      </span>
      <span class="name">{{ preset.name }}</span>
    </div>

    <div
      class="g-menu-item"
      role="button"
      tabindex="0"
      (click)="openRenameDialog(preset, $event)"
      (keydown.enter)="openRenameDialog(preset, $event)">
      <i [class]="ICONS.pencil"></i>
      {{ 'features.data.filter_presets.rename_action' | translate }}
    </div>

    @if (!preset.template) {
      <div
        class="g-menu-item"
        [class.is-disabled]="!presetsStore.hasFiltersToSave()"
        role="button"
        tabindex="0"
        (click)="saveOver(preset)"
        (keydown.enter)="saveOver(preset)">
        <i [class]="ICONS.floppyDisk"></i>
        {{ 'features.data.filter_presets.overwrite' | translate }}
      </div>

      <div
        class="g-menu-item"
        role="button"
        tabindex="0"
        (click)="toggleDefault(preset, $event)"
        (keydown.enter)="toggleDefault(preset, $event)">
        <i [class]="ICONS.pushPin"></i>
        {{
          (preset.isDefault
            ? 'features.data.filter_presets.unset_default'
            : 'features.data.filter_presets.set_default'
          ) | translate
        }}
      </div>

      @if (preset.scope === FilterPresetScope.DATASPACE) {
        <p class="g-menu-note">
          {{ 'features.data.filter_presets.shared_default_hint' | translate }}
        </p>
      }
    }

    <div class="g-menu-divider"></div>

    <div
      class="g-menu-item is-danger"
      role="button"
      tabindex="0"
      (click)="openDeleteDialog(preset, $event)"
      (keydown.enter)="openDeleteDialog(preset, $event)">
      <i [class]="ICONS.trash"></i>
      {{ 'common.delete' | translate }}
    </div>
  </div>
</ng-template>

@if (dialog(); as dialogState) {
  <app-modal
    [config]="dialogConfig()"
    [title]="dialogTitleKey(dialogState.mode) | translate"
    (closeModal)="closeDialog()">
    <div modal-body class="flex flex-col gap-3.5">
      @if (dialogState.mode === 'delete') {
        <p class="text-text-secondary text-sm">
          {{
            'features.data.filter_presets.dialog.delete_description'
              | translate: { name: dialogPresetName() }
          }}
        </p>
      } @else {
        <div class="flex flex-col" [formGroup]="dialogForm">
          <app-input
            formControlName="name"
            [required]="true"
            [label]="'features.data.filter_presets.dialog.name_label' | translate"
            [placeholder]="
              'features.data.filter_presets.dialog.name_placeholder' | translate
            "
            [helpText]="'features.data.filter_presets.dialog.name_hint' | translate" />
        </div>

        @if (dialogState.mode === 'create' && currentCriteria().length > 0) {
          <div>
            <p
              class="text-text-tertiary mb-1.5 text-[10px] font-semibold tracking-wider uppercase">
              {{ 'features.data.filter_presets.dialog.criteria_label' | translate }}
            </p>
            <div class="flex flex-wrap gap-1.5">
              @for (criterion of currentCriteria(); track $index) {
                <span
                  class="border-default text-2xs inline-flex items-stretch overflow-hidden rounded-xs border">
                  <span class="bg-surface px-1.5 py-0.5 font-semibold">
                    {{ criterion.field }}
                  </span>
                  <span class="border-weak text-text-secondary border-l px-1.5 py-0.5">
                    {{ criterion.operation }}
                  </span>
                  @if (criterion.value) {
                    <span
                      class="border-weak text-accent-main border-l px-1.5 py-0.5 font-mono">
                      {{ criterion.value }}
                    </span>
                  }
                </span>
              }
            </div>
          </div>
        }

        @if (dialogState.mode === 'create' && currentPathFilterText(); as dsl) {
          <div>
            <p
              class="text-text-tertiary mb-1.5 text-[10px] font-semibold tracking-wider uppercase">
              {{ 'features.data.filter_presets.dialog.path_filter_label' | translate }}
            </p>
            <p class="border-default bg-surface rounded-xs border px-1.5 py-1">
              <app-dsl-text [text]="dsl" />
            </p>
          </div>
        }

        @if (dialogState.mode === 'create' && currentSortSummary().length > 0) {
          <div>
            <p
              class="text-text-tertiary mb-1.5 text-[10px] font-semibold tracking-wider uppercase">
              {{ 'features.data.filter_presets.dialog.sort_label' | translate }}
            </p>
            <div class="flex flex-wrap gap-1.5">
              @for (sorted of currentSortSummary(); track $index) {
                <span
                  class="border-default text-2xs inline-flex items-stretch overflow-hidden rounded-xs border">
                  <span class="bg-surface px-1.5 py-0.5 font-semibold">
                    {{ sorted.field }}
                  </span>
                  <span class="border-weak text-accent-main border-l px-1.5 py-0.5">
                    {{ sorted.arrow }}
                  </span>
                </span>
              }
            </div>
          </div>
        }

        @if (dialogState.mode === 'create') {
          <div class="flex flex-col gap-2">
            @if (presetsStore.hasSortToSave()) {
              <app-checkbox
                align="start"
                [checked]="dialogForm.controls.includeSort.value"
                [onChange]="setIncludeSort">
                <span class="text-text-primary ml-2 text-[12.5px] font-medium">
                  {{ 'features.data.filter_presets.dialog.include_sort_label' | translate }}
                  <span class="text-text-tertiary mt-0.5 block text-[11.5px] font-normal">
                    {{
                      'features.data.filter_presets.dialog.include_sort_hint' | translate
                    }}
                  </span>
                </span>
              </app-checkbox>
            }

            @if (presetsStore.canSaveForContext()) {
              <app-checkbox
                align="start"
                [checked]="dialogForm.controls.contextScoped.value"
                [onChange]="setContextScoped">
                <span class="text-text-primary ml-2 text-[12.5px] font-medium">
                  {{
                    'features.data.filter_presets.dialog.context_label'
                      | translate: { field: presetsStore.contextField() }
                  }}
                  <span class="text-text-tertiary mt-0.5 block text-[11.5px] font-normal">
                    {{ 'features.data.filter_presets.dialog.context_hint' | translate }}
                  </span>
                </span>
              </app-checkbox>
            }

            @if (presetsStore.canShareWithDataspace()) {
              <app-checkbox
                align="start"
                [checked]="dialogForm.controls.shareWithDataspace.value"
                [onChange]="setShareWithDataspace">
                <span class="text-text-primary ml-2 text-[12.5px] font-medium">
                  {{ 'features.data.filter_presets.dialog.share_label' | translate }}
                  <span class="text-text-tertiary mt-0.5 block text-[11.5px] font-normal">
                    {{ 'features.data.filter_presets.dialog.share_hint' | translate }}
                  </span>
                </span>
              </app-checkbox>
            }

            <app-checkbox
              align="start"
              [checked]="dialogForm.controls.asDefault.value"
              [onChange]="setAsDefault">
              <span class="text-text-primary ml-2 text-[12.5px] font-medium">
                {{ 'features.data.filter_presets.dialog.default_label' | translate }}
                <span class="text-text-tertiary mt-0.5 block text-[11.5px] font-normal">
                  {{ 'features.data.filter_presets.dialog.default_hint' | translate }}
                </span>
              </span>
            </app-checkbox>
          </div>
        }
      }

      @if (dialogState.error; as error) {
        <p class="flex items-start gap-1.5 text-xs text-red-500">
          <i [class]="ICONS.warningCircle" class="mt-0.5 shrink-0"></i>
          {{ error }}
        </p>
      }
    </div>
  </app-modal>
}
